// =============================================================================
// PEDRA — src/lib/paste.ts
// Converte texto colado (Excel, Google Sheets, CSV, TXT) em `Part[]` /
// `StockSheet[]`.
//
// Cobre o que o usuario brasileiro realmente cola:
//   - TAB (colagem direta de planilha), ';' (CSV do Excel pt-BR), ',' e '|'
//   - virgula decimal ("2.400,5") e ponto decimal ("2400.5")
//   - aspas do RFC 4180, inclusive quebra de linha dentro de campo
//   - cabecalho opcional, em pt-BR ou em ingles, em qualquer ordem
//   - sem cabecalho: ordem posicional do contrato
//     comprimento, largura, qtd, nome, material, veio, ativa
// =============================================================================

import type { GrainOrientation, Part, Settings, StockSheet } from '../core/types';
import { colorForIndex, emptyEdgeBanding, newId } from '../core/defaults';
import { parseDimension } from '../core/units';
import { isValidHexColor, LIMITS } from '../core/validation';
import { normalizeText } from './format';

// -----------------------------------------------------------------------------
// TIPOS
// -----------------------------------------------------------------------------

/** Separadores de coluna reconhecidos. */
export type Delimiter = '\t' | ';' | ',' | '|';

/** Coluna canonica reconhecida no cabecalho. */
export type ColumnKey =
  | 'length'
  | 'width'
  | 'qty'
  | 'label'
  | 'material'
  | 'grain'
  | 'enabled'
  | 'color'
  | 'dims'
  | 'ignore';

export interface PasteResult<T> {
  rows: T[];
  /** Quantas linhas de dados foram descartadas. */
  skipped: number;
  /** Mensagens em pt-BR prontas para exibir. */
  errors: string[];
}

/** Ordem posicional usada quando NAO ha cabecalho (definida no contrato). */
export const DEFAULT_COLUMNS: readonly ColumnKey[] = Object.freeze([
  'length',
  'width',
  'qty',
  'label',
  'material',
  'grain',
  'enabled',
]);

/** Maximo de mensagens de erro devolvidas antes de resumir. */
const MAX_ERRORS = 12;

// -----------------------------------------------------------------------------
// TOKENIZACAO
// -----------------------------------------------------------------------------

const DELIMITERS: readonly Delimiter[] = ['\t', ';', '|', ','];

/** Remove BOM e normaliza aspas tipograficas que o Word/Excel inserem. */
function preclean(text: string): string {
  return (text ?? '').replace(/^\uFEFF/, '');
}

/**
 * Conta ocorrencias de um caractere fora de aspas, na linha inteira.
 */
function countOutsideQuotes(line: string, ch: string): number {
  let n = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && c === ch) n++;
  }
  return n;
}

/**
 * Detecta o separador de colunas.
 *
 * 1. Qualquer TAB no texto vence — planilha sempre cola com TAB.
 * 2. Depois, na ordem `;` `|` `,`, o primeiro que aparecer fora de aspas.
 * 3. Nada encontrado -> `;` (o texto vira uma coluna so, e o erro fica claro).
 *
 * Refinamento pt-BR: se o candidato for `,` mas TODAS as linhas parecerem
 * apenas um numero com virgula decimal (`2.400,5`), devolvemos `;` para nao
 * quebrar o numero ao meio.
 */
export function detectDelimiter(text: string): Delimiter {
  const lines = splitRows(preclean(text));
  if (lines.length === 0) return ';';

  for (const d of DELIMITERS) {
    let total = 0;
    for (const line of lines) total += countOutsideQuotes(line, d);
    if (total === 0) continue;
    if (d === ',' && looksLikeDecimalOnly(lines)) return ';';
    return d;
  }
  return ';';
}

/** Todas as linhas sao do tipo `1234,5` (numero com virgula decimal)? */
function looksLikeDecimalOnly(lines: readonly string[]): boolean {
  const re = /^\s*-?[\d.]+\s*,\s*\d{1,3}\s*$/;
  let seen = 0;
  for (const line of lines) {
    if (line.trim() === '') continue;
    if (!re.test(line)) return false;
    seen++;
  }
  return seen > 0;
}

/**
 * Divide o texto em linhas respeitando aspas (uma quebra de linha dentro de
 * um campo entre aspas NAO separa registros). Linhas em branco somem.
 */
export function splitRows(text: string): string[] {
  const src = preclean(text);
  const out: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const c = src[i] as string;
    if (c === '"') {
      if (inQuotes && src[i + 1] === '"') {
        current += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      current += c;
      continue;
    }
    if (!inQuotes && (c === '\n' || c === '\r')) {
      if (c === '\r' && src[i + 1] === '\n') i++;
      if (current.trim() !== '') out.push(current);
      current = '';
      continue;
    }
    current += c;
  }
  if (current.trim() !== '') out.push(current);
  return out;
}

/**
 * Divide uma linha em celulas seguindo o RFC 4180:
 * aspas abrem campo literal, `""` vira uma aspa, delimitador dentro de aspas
 * e texto comum. As celulas voltam ja desescapadas e aparadas.
 */
export function splitLine(line: string, delim: Delimiter): string[] {
  const out: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const c = line[i] as string;
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += c;
      }
      continue;
    }
    if (c === '"' && cell.trim() === '') {
      inQuotes = true;
      cell = '';
      continue;
    }
    if (c === delim) {
      out.push(cell.trim());
      cell = '';
      continue;
    }
    if (c === '\r') continue;
    cell += c;
  }
  out.push(cell.trim());
  return out;
}

// -----------------------------------------------------------------------------
// CABECALHO
// -----------------------------------------------------------------------------

/**
 * Tabela de tokens -> coluna. A ORDEM importa:
 *  - `width` antes de `length` ("Largura" nunca pode cair em comprimento);
 *  - `dims` (plural "Medidas") antes de `length` (singular "Medida");
 *  - `qty` ("Peças") antes de `label` ("Peça").
 */
const HEADER_TOKENS: ReadonlyArray<readonly [ColumnKey, readonly string[]]> = [
  ['width', ['larg', 'width']],
  ['dims', ['dimensoes', 'medidas', 'tamanho', 'axb', 'lxc', 'cxl']],
  ['length', ['compr', 'length', 'altura', 'alt', 'height', 'medida', 'dimensao']],
  ['qty', ['qtd', 'qty', 'quant', 'count', 'copia', 'copies', 'pecas', 'num', 'unidades']],
  ['enabled', ['habilit', 'enabled', 'ativ', 'active', 'usar', 'ligad']],
  ['material', ['material', 'chapa', 'mat.']],
  ['label', ['label', 'rotulo', 'nome', 'name', 'descri', 'ident', 'ambiente', 'peca', 'titulo']],
  ['grain', ['veio', 'grain', 'orient', 'movimento', 'sentido']],
  ['color', ['cor', 'color']],
];

/**
 * Casa uma celula de cabecalho com uma coluna canonica.
 * Devolve `null` quando nao reconhece (a coluna sera ignorada).
 */
export function mapHeaderCell(raw: string): ColumnKey | null {
  const t = normalizeText(raw)
    .replace(/[()[\]"']/g, '')
    .replace(/\s+/g, '');
  if (t === '') return null;
  for (const [key, tokens] of HEADER_TOKENS) {
    for (const token of tokens) {
      if (t.includes(token)) return key;
    }
  }
  return null;
}

/**
 * Mapeia a linha de cabecalho inteira. `confidence` de 0 a 1 indica quanto do
 * cabecalho foi entendido (a interface pode pedir mapeamento manual abaixo de 0,6).
 */
export function mapColumns(header: readonly string[]): { map: ColumnKey[]; confidence: number } {
  const map: ColumnKey[] = [];
  const used = new Set<ColumnKey>();
  let hits = 0;

  for (const cell of header) {
    const key = mapHeaderCell(cell);
    // Coluna repetida (dois "Nome") — so a primeira vale.
    if (key === null || used.has(key)) {
      map.push('ignore');
      continue;
    }
    used.add(key);
    map.push(key);
    hits++;
  }

  let confidence = header.length === 0 ? 0 : hits / header.length;
  if (used.has('width') && used.has('length')) confidence = Math.min(1, confidence + 0.2);
  return { map, confidence };
}

/** Uma celula de cabecalho nunca comeca por digito ou sinal de numero. */
function looksNumericCell(cell: string): boolean {
  return /^[\d.,+-]/.test(cell.trim());
}

/**
 * Heuristica de "esta linha e cabecalho?": pelo menos duas celulas
 * preenchidas, NENHUMA parecendo numero e pelo menos DUAS reconhecidas — em
 * qualquer posicao.
 *
 * A versao antiga exigia que as duas PRIMEIRAS celulas fossem reconhecidas.
 * Bastava uma coluna nossa nao conhecer ("Comprimento;Espessura;Qtd") para o
 * cabecalho virar linha de dados e a espessura ser lida como largura.
 */
export function looksLikeHeader(cells: readonly string[]): boolean {
  const filled = cells.map((c) => c.trim()).filter((c) => c !== '');
  if (filled.length < 2) return false;
  if (filled.some(looksNumericCell)) return false;

  let known = 0;
  for (const cell of filled) {
    if (mapHeaderCell(cell) !== null) known++;
  }
  return known >= 2;
}

/** Celulas de cabecalho preenchidas que nao casaram com nenhuma coluna. */
export function unknownHeaderCells(cells: readonly string[]): string[] {
  return cells
    .map((c) => c.trim())
    .filter((c) => c !== '' && mapHeaderCell(c) === null);
}

// -----------------------------------------------------------------------------
// CONVERSORES DE CELULA
// -----------------------------------------------------------------------------

/** Quantidade de peca: vazio = 1, sempre inteiro dentro dos limites. */
export function parseQty(raw: string): number {
  const s = (raw ?? '').trim();
  if (s === '') return 1;
  const digits = s.replace(/[^\d-]/g, '');
  const n = Number.parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 0) return 1;
  return Math.min(n, LIMITS.MAX_QTY);
}

/** Resultado da leitura da quantidade de chapa. `qty: null` = estoque infinito. */
export type StockQtyRead = { ok: true; qty: number | null } | { ok: false; error: string };

/**
 * Quantidade de chapa, distinguindo ILIMITADO de INVALIDO.
 *
 * `null` (ilimitado) so pode sair de vazio, "-", "∞" ou "ilimitado". Antes,
 * qualquer lixo — inclusive "-3" — caia no mesmo `null`, e colar uma
 * quantidade negativa cadastrava uma chapa de estoque infinito.
 */
export function readStockQty(raw: string): StockQtyRead {
  const s = (raw ?? '').trim();
  if (s === '') return { ok: true, qty: null };

  const t = normalizeText(s);
  if (t === '-' || t === '∞' || t === 'inf' || t.startsWith('ilimitad')) {
    return { ok: true, qty: null };
  }

  const digits = s.replace(/[^\d-]/g, '');
  const negative = digits.includes('-');
  const clean = digits.replace(/-/g, '');
  if (clean === '') {
    return {
      ok: false,
      error: `quantidade "${s}" não é um número — deixe a célula em branco para estoque ilimitado.`,
    };
  }
  if (negative) {
    return {
      ok: false,
      error: `quantidade "${s}" é negativa — use 0 ou deixe a célula em branco para estoque ilimitado.`,
    };
  }

  const n = Number.parseInt(clean, 10);
  if (!Number.isFinite(n)) {
    return {
      ok: false,
      error: `quantidade "${s}" não é um número — deixe a célula em branco para estoque ilimitado.`,
    };
  }
  return { ok: true, qty: Math.min(n, LIMITS.MAX_QTY) };
}

/**
 * Compatibilidade: quantidade de chapa como numero ou `null`.
 * Prefira `readStockQty`, que separa "ilimitado" de "invalido".
 */
export function parseStockQty(raw: string): number | null {
  const r = readStockQty(raw);
  return r.ok ? r.qty : null;
}

const FALSE_TOKENS = new Set([
  'false',
  'f',
  'no',
  'n',
  '0',
  'nao',
  'falso',
  'desativado',
  'desativada',
  'inativo',
  'inativa',
  'off',
  'desligado',
  '-',
]);

/** Booleano tolerante. Vazio = `true` (a linha colada nasce ativa). */
export function parseBoolean(raw: string): boolean {
  const s = (raw ?? '').trim();
  if (s === '') return true;
  return !FALSE_TOKENS.has(normalizeText(s));
}

const GRAIN_WIDTH = ['h', 'hor', 'horizontal', 'w', 'width', 'larg', '1'];
const GRAIN_LENGTH = ['v', 'ver', 'vert', 'vertical', 'length', 'compr', 'alt', '2'];

/** Direcao do veio a partir de texto livre. Vazio / desconhecido = 0. */
export function parseGrain(raw: string): GrainOrientation {
  const t = normalizeText(raw);
  if (t === '') return 0;
  if (GRAIN_WIDTH.includes(t)) return 1;
  if (GRAIN_LENGTH.includes(t)) return 2;
  if (GRAIN_WIDTH.some((token) => token.length > 1 && t.startsWith(token))) return 1;
  if (GRAIN_LENGTH.some((token) => token.length > 1 && t.startsWith(token))) return 2;
  return 0;
}

/** `"2400x1200"`, `"2.400 × 1.200"`, `"2400*1200"` -> par de medidas. */
export function parseDimensionPair(
  raw: string,
  unit: Settings['unit'],
): { a: number; b: number } | null {
  const parts = (raw ?? '').split(/\s*[x×*]\s*/i).filter((p) => p.trim() !== '');
  if (parts.length !== 2) return null;
  const a = parseDimension(parts[0] as string, unit);
  const b = parseDimension(parts[1] as string, unit);
  if (a === null || b === null) return null;
  return { a, b };
}

// -----------------------------------------------------------------------------
// LEITURA DA TABELA
// -----------------------------------------------------------------------------

export interface ParsedTable {
  delimiter: Delimiter;
  hasHeader: boolean;
  map: ColumnKey[];
  /** Linhas de dados ja divididas em celulas (sem o cabecalho). */
  rows: string[][];
  /** Numero da linha original (1-based) de cada `rows[i]`, para as mensagens. */
  lineNumbers: number[];
  errors: string[];
}

/** `"A"`, `"A" e "B"`, `"A", "B" e "C"` — enumeracao em pt-BR. */
function quoteList(items: readonly string[]): string {
  const quoted = items.map((c) => `"${c}"`);
  if (quoted.length <= 1) return quoted.join('');
  return `${quoted.slice(0, -1).join(', ')} e ${quoted[quoted.length - 1] as string}`;
}

/** Divide o texto colado numa tabela com colunas ja mapeadas. */
export function parseTable(text: string, fallback: readonly ColumnKey[] = DEFAULT_COLUMNS): ParsedTable {
  const errors: string[] = [];
  const delimiter = detectDelimiter(text);
  const lines = splitRows(text);

  if (lines.length === 0) {
    return { delimiter, hasHeader: false, map: [...fallback], rows: [], lineNumbers: [], errors };
  }

  const first = splitLine(lines[0] as string, delimiter);
  const hasHeader = looksLikeHeader(first);
  const map = hasHeader ? mapColumns(first).map : [...fallback];

  // O usuario precisa saber o que foi descartado — senao uma coluna
  // "Espessura" some sem deixar rastro e ele acha que importou tudo.
  if (hasHeader) {
    const unknown = unknownHeaderCells(first);
    if (unknown.length === 1) {
      errors.push(`A coluna ${quoteList(unknown)} não foi reconhecida e será ignorada.`);
    } else if (unknown.length > 1) {
      errors.push(`As colunas ${quoteList(unknown)} não foram reconhecidas e serão ignoradas.`);
    }
  }

  const dataLines = hasHeader ? lines.slice(1) : lines;
  let limited = dataLines;
  if (dataLines.length > LIMITS.MAX_ROWS) {
    limited = dataLines.slice(0, LIMITS.MAX_ROWS);
    errors.push(
      `O texto colado tem ${dataLines.length} linhas. Só as primeiras ${LIMITS.MAX_ROWS} foram importadas.`,
    );
  }

  const rows: string[][] = [];
  const lineNumbers: number[] = [];
  const offset = hasHeader ? 2 : 1;
  for (let i = 0; i < limited.length; i++) {
    const cells = splitLine(limited[i] as string, delimiter);
    if (cells.every((c) => c === '')) continue;
    rows.push(cells);
    lineNumbers.push(i + offset);
  }

  return { delimiter, hasHeader, map, rows, lineNumbers, errors };
}

/** Extrai os valores da linha por coluna canonica. */
function pick(cells: readonly string[], map: readonly ColumnKey[]): Partial<Record<ColumnKey, string>> {
  const out: Partial<Record<ColumnKey, string>> = {};
  for (let i = 0; i < cells.length; i++) {
    const key = map[i];
    if (!key || key === 'ignore') continue;
    const value = (cells[i] ?? '').trim();
    if (value === '') continue;
    if (out[key] === undefined) out[key] = value;
  }
  return out;
}

/** Acrescenta uma mensagem respeitando o teto de erros exibidos. */
function pushError(errors: string[], message: string): void {
  if (errors.length < MAX_ERRORS) errors.push(message);
}

/** Fecha a lista de erros com o resumo do que sobrou. */
function finishErrors(errors: string[], total: number): string[] {
  if (total > errors.length) {
    return [...errors, `… e mais ${total - errors.length} problema(s) semelhantes.`];
  }
  return errors;
}

/** Le comprimento e largura de uma linha, aplicando a coluna combinada `dims`. */
function readDimensions(
  values: Partial<Record<ColumnKey, string>>,
  settings: Settings,
): { length: number; width: number; note: string | null } | { error: string } {
  let lengthRaw = values.length ?? '';
  let widthRaw = values.width ?? '';

  if (values.dims && (lengthRaw === '' || widthRaw === '')) {
    const pair = parseDimensionPair(values.dims, settings.unit);
    if (pair) {
      const first = settings.dimensionOrder === 'widthFirst' ? pair.b : pair.a;
      const second = settings.dimensionOrder === 'widthFirst' ? pair.a : pair.b;
      return { length: first, width: second, note: null };
    }
    return { error: `medida "${values.dims}" não pôde ser lida.` };
  }

  if (lengthRaw === '' && widthRaw !== '') {
    lengthRaw = widthRaw;
    widthRaw = '';
  }

  const length = parseDimension(lengthRaw, settings.unit);
  if (length === null) {
    return {
      error:
        lengthRaw === ''
          ? 'comprimento em branco.'
          : `comprimento "${lengthRaw}" não é uma medida válida.`,
    };
  }
  if (length <= 0 || length > LIMITS.MAX_DIMENSION) {
    return { error: `comprimento "${lengthRaw}" está fora da faixa aceita.` };
  }

  const width = widthRaw === '' ? null : parseDimension(widthRaw, settings.unit);
  if (width === null) {
    return {
      length,
      width: length,
      note:
        widthRaw === ''
          ? 'largura em branco — assumimos peça quadrada.'
          : `largura "${widthRaw}" não pôde ser lida — assumimos peça quadrada.`,
    };
  }
  if (width <= 0 || width > LIMITS.MAX_DIMENSION) {
    return { error: `largura "${widthRaw}" está fora da faixa aceita.` };
  }
  return { length, width, note: null };
}

// -----------------------------------------------------------------------------
// API PUBLICA
// -----------------------------------------------------------------------------

/**
 * Converte texto colado em pecas. Nunca lanca: o que nao dava para ler vira
 * mensagem em `errors` e a linha entra em `skipped`.
 */
export function parsePastedParts(text: string, settings: Settings): PasteResult<Part> {
  const rows: Part[] = [];
  if (preclean(text).trim() === '') {
    return { rows, skipped: 0, errors: ['Nada foi colado. Copie as células da planilha e cole aqui.'] };
  }

  const table = parseTable(text);
  const errors: string[] = [...table.errors];
  let problems = table.errors.length;
  let skipped = 0;

  for (let i = 0; i < table.rows.length; i++) {
    const cells = table.rows[i] as string[];
    const line = table.lineNumbers[i] as number;
    const values = pick(cells, table.map);
    const dims = readDimensions(values, settings);

    if ('error' in dims) {
      skipped++;
      problems++;
      pushError(errors, `Linha ${line} ignorada: ${dims.error}`);
      continue;
    }
    if (dims.note) {
      problems++;
      pushError(errors, `Linha ${line}: ${dims.note}`);
    }

    const colorRaw = values.color ?? '';
    const color = isValidHexColor(colorRaw) ? colorRaw : colorForIndex(rows.length);

    rows.push({
      id: newId('p'),
      length: dims.length,
      width: dims.width,
      qty: parseQty(values.qty ?? ''),
      label: (values.label ?? '').slice(0, LIMITS.MAX_LABEL_LENGTH),
      material: (values.material ?? '').slice(0, LIMITS.MAX_NAME_LENGTH),
      orientation: parseGrain(values.grain ?? ''),
      enabled: parseBoolean(values.enabled ?? ''),
      edgeBanding: emptyEdgeBanding(),
      color,
    });
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('Nenhuma linha válida foi encontrada no texto colado.');
  }

  return { rows, skipped, errors: finishErrors(errors, problems) };
}

/**
 * Converte texto colado em chapas de estoque. Quantidade em branco significa
 * estoque ILIMITADO (`qty: null`), igual ao comportamento da tabela.
 */
export function parsePastedStock(text: string, settings: Settings): PasteResult<StockSheet> {
  const rows: StockSheet[] = [];
  if (preclean(text).trim() === '') {
    return { rows, skipped: 0, errors: ['Nada foi colado. Copie as células da planilha e cole aqui.'] };
  }

  const table = parseTable(text);
  const errors: string[] = [...table.errors];
  let problems = table.errors.length;
  let skipped = 0;

  for (let i = 0; i < table.rows.length; i++) {
    const cells = table.rows[i] as string[];
    const line = table.lineNumbers[i] as number;
    const values = pick(cells, table.map);
    const dims = readDimensions(values, settings);

    if ('error' in dims) {
      skipped++;
      problems++;
      pushError(errors, `Linha ${line} ignorada: ${dims.error}`);
      continue;
    }
    if (dims.note) {
      problems++;
      pushError(errors, `Linha ${line}: ${dims.note}`);
    }

    const qty = readStockQty(values.qty ?? '');
    if (!qty.ok) {
      skipped++;
      problems++;
      pushError(errors, `Linha ${line} ignorada: ${qty.error}`);
      continue;
    }

    rows.push({
      id: newId('s'),
      length: dims.length,
      width: dims.width,
      qty: qty.qty,
      label: (values.label ?? '').slice(0, LIMITS.MAX_LABEL_LENGTH),
      material: (values.material ?? '').slice(0, LIMITS.MAX_NAME_LENGTH),
      orientation: parseGrain(values.grain ?? ''),
      enabled: parseBoolean(values.enabled ?? ''),
    });
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('Nenhuma linha válida foi encontrada no texto colado.');
  }

  return { rows, skipped, errors: finishErrors(errors, problems) };
}
